import { useCallback, useEffect, useMemo, useState } from 'react'
import { ButtonOrLoader } from '../../../../../../../../baseComponents/Button'
import { ButtonLoadFiltersStateLess, FiltersConfig, useButtonsLoadFiltersStateFullLogic } from '../../../../../../../../baseComponents/Button/ButtonLoadFilter'
import { ModalMedium } from '../../../../../../../../baseComponents/Modal'
import { ModalComponentPropsWithoutSubmit } from '../../../../../../../../baseComponents/ModalsLayouts/types'
import VirtualTableListing from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing'
import { DeleteRow } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import SearchBarProduct from '../../../../../../../../specificComponents/Product/SearchBarProduct'
import { OnChangeSearchBarAddProduct } from '../../../../../../../../specificComponents/Product/SearchBarProduct/types'
import { ProductExtendedTyLong, ProductExtendedTyShort } from '../../../../../../../../specificComponents/Product/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import useIsLoading from '../../../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getTableSelectedColumns, SelectedColumnsAndFilters } from '../../../../../../../../utils/hooks/useConfigColumns'
import { changeEmployeeParams } from '../../../../../../../../utils/reducers/reduxDispatch'
import { TypeSellTableNameTy } from '../types'

type ModalConfigStatsByProductProps = ModalConfigStatsByProductLogicProps & {
    selectedColumnsAndFilters: SelectedColumnsAndFilters
    typeTableName: TypeSellTableNameTy
}

const ModalConfigStatsByProduct: React.FC<ModalConfigStatsByProductProps> = ({ setOpened, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeTableName }) => {
    const { deleteRow, loading, changeData, onSubmitHandler, mySelectedProducts, isCustomFilter, copySelectedColumns, myOnChangeFilter } = useModalConfigStatsByProductLogic({ setOpened, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeTableName })
    const { t } = useTwinTranslation()
    const name = isCustomFilter && copySelectedColumns?.name ? copySelectedColumns?.name : t('byDefault', 'Por defecto')
    const mySelectedFilter: FiltersConfig = { value: copySelectedColumns?.filters?.ProductId?.value, id: copySelectedColumns?.id, name: copySelectedColumns?.name }
    const defaultValues = getTableSelectedColumns(typeTableName +'_product_config_stats', [])
    const { filters, getAllFilters } = useButtonsLoadFiltersStateFullLogic({
        tableName: 'table_' + typeTableName + '_product_config_stats', defaultValues })
    const columns: ColumnTableSchema<ProductExtendedTyLong> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            width: 50,
        },
        {
            id: 'name',
            dataKey: 'ProductLangs.name',
            label: t('name', 'Nombre'),
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + allRowData?.rowIndex} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => deleteRow(allRowData?.id)} />,
        }
    ]
    return (
        <ModalMedium opened={true} setOpened={setOpened} className='flex flex-col '>
            <div className='flex items-center flex-wrap gap-10 mb-20'>
                <span className='medium16'><TwinTrans transKey='configuration'>Configuración</TwinTrans>: {name}</span>
                <ButtonLoadFiltersStateLess selectedFilters={mySelectedFilter} onChangeFilter={myOnChangeFilter} tableName={'table_'+ typeTableName +'_product_config_stats'} allowCreate={true} defaultValues={defaultValues} filters={filters} getAllFilters={getAllFilters} onOpenModal={getAllFilters} />
            </div>
            <SearchBarProduct changeSelectedItems={changeData} excludeIds={copySelectedColumns.filters?.ProductId?.value || []} fieldNotIn='Product.id' name='ProductQuote' url='/api/app/product/getAllProducts' id={''} placeholder={t('searchProductDots', 'Buscar producto...')}  where={{}} />
                <div className='flex-auto table_container mt-20'>
                <VirtualTableListing tableData={mySelectedProducts || []} columns={columns} name='modalConfigStatsByProduct' />
                </div>
                <div className='flex justify-end'>
                <ButtonOrLoader onClick={onSubmitHandler} buttonIsDisabled={loading} textButton={t('save', 'Guardar')} className='mt-68' />
                </div>
        </ModalMedium>
    )
}

interface ModalConfigStatsByProductLogicProps extends ModalComponentPropsWithoutSubmit{
    typeTableName: TypeSellTableNameTy
    selectedColumnsAndFilters: SelectedColumnsAndFilters
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
}

const useModalConfigStatsByProductLogic = ({ setOpened, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeTableName }: ModalConfigStatsByProductLogicProps) => {
    const { loading, startLoading } = useIsLoading()
    const [copySelectedColumns, setCopySelectedColumns] = useState<SelectedColumnsAndFilters>(JSON.parse(JSON.stringify(selectedColumnsAndFilters)))
    const [mySelectedProducts, setMySelectedProducts] = useState<ProductExtendedTyShort[] | null>(null)
    const isCustomFilter = copySelectedColumns?.id && String(copySelectedColumns?.id) !== '0'
    const name = typeTableName+'_product_config_stats'
    const mySelectedIds = useMemo(() => copySelectedColumns?.filters?.ProductId?.value || [], [copySelectedColumns?.filters?.ProductId?.value])
    const getAllProductsParameters = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/product/getAllProducts', {
            attributes: ['id', 'reference', 'ProductLangs.name'],
            where: {
                'Product.id': {
                    $in: mySelectedIds
                }
            }
        })

        if (result) {
            setMySelectedProducts(result)
        }
        
    }, [setMySelectedProducts, mySelectedIds])


    const deleteRow = useCallback((id: number) => {
        setCopySelectedColumns((oldData) => {
            const copy = JSON.parse(JSON.stringify(oldData))
            const posId = copy.filters?.ProductId?.value.indexOf(id)
            if (posId !== -1) {
                copy.filters?.ProductId?.value?.splice(posId, 1)
            }
            return { ...copy }
        })
    }, [setCopySelectedColumns])
    const changeData: OnChangeSearchBarAddProduct = useCallback((item) => {
        setCopySelectedColumns((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            if (!copy.filters.ProductId) {
                copy.filters.ProductId = { value: []}
            }
            copy.filters?.ProductId?.value?.push(item?.id)
            return { ...copy }
        }
        )
    }, [setCopySelectedColumns])

    const updateFilterOnTable = useCallback(async (columns: SelectedColumnsAndFilters, id: number) => {
        delete columns.id
        delete columns.name
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/filter/updateFilter', { id, value: myValue })
    }, [])

    const updateEmployeeParams = useCallback(async (columns: SelectedColumnsAndFilters) => {
        delete columns?.id
        delete columns?.name
        const myName = 'table_' + name
        if(columns.filters.startDate){
            delete columns.filters.startDate
        }
        if(columns.filters.endDate){
            delete columns.filters.endDate
        }
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValue })
        changeEmployeeParams({ [myName]: myValue })
    }, [name])

    const updateParamsDecider = useCallback(async (columns: SelectedColumnsAndFilters) => {
        if (copySelectedColumns?.id && String(copySelectedColumns?.id) !== '0') {
            await updateFilterOnTable(columns, copySelectedColumns?.id)
        } else {
            await updateEmployeeParams(columns)
        }
    }, [copySelectedColumns?.id, updateFilterOnTable, updateEmployeeParams])

    const onSubmitHandler = useCallback(async () => {
        startLoading()
        const mySelectedColumns = JSON.parse(JSON.stringify(copySelectedColumns))
        await updateParamsDecider(copySelectedColumns)
        setSelectedColumnsAndFilters(mySelectedColumns)
        setOpened(null)
        return false
    }, [copySelectedColumns, setSelectedColumnsAndFilters, setOpened, updateParamsDecider, startLoading])

    const myOnChangeFilter = useCallback((json: TwinDictionary) => {
        setCopySelectedColumns({ filters: json?.value?.filters || {}, selectedColumns: json?.value?.selectedColumns || {}, id: json?.id, name: json?.name, order: json?.value?.order || {} })
    }, [setCopySelectedColumns])

    useEffect(() => {
        getAllProductsParameters()
    }, [getAllProductsParameters])

    
    return { deleteRow, loading, changeData, onSubmitHandler, mySelectedProducts, isCustomFilter, copySelectedColumns, myOnChangeFilter }
}

export default ModalConfigStatsByProduct